/**
 * Shape of a response from the PHP backend's /request/xApi/yAction endpoints.
 * Every API response follows this envelope regardless of the endpoint.
 */
export interface ApiResponse<TData = unknown> {
  success: boolean;
  msg: string;
  status: {
    code: number | string;
    message: string;
  };
  formattedErrors: Array<string | Record<string, string>> | false;
  errors: Record<string, string> | string[] | false;
  warnings: Record<string, string> | string[] | false;
  data: TData;
  messages: Array<{ type?: string; message?: string }>;
}

/**
 * Thrown by the API client when a request succeeds at the HTTP level but
 * the response envelope reports {success: false}. Carries the envelope
 * so callers can inspect validation errors, warnings, etc.
 */
export class ApiError extends Error {
  public readonly response: ApiResponse<unknown>;

  constructor(message: string, response: ApiResponse<unknown>) {
    super(message);
    this.name = 'ApiError';
    this.response = response;
  }

  /** Flat list of error messages, useful for showing to the user. */
  public get errorList(): string[] {
    const out: string[] = [];
    if (Array.isArray(this.response.errors)) {
      out.push(...this.response.errors);
    } else if (this.response.errors && typeof this.response.errors === 'object') {
      out.push(...Object.values(this.response.errors));
    }
    if (Array.isArray(this.response.formattedErrors)) {
      for (const err of this.response.formattedErrors) {
        if (typeof err === 'string') out.push(err);
        else if (err && typeof err === 'object') out.push(...Object.values(err));
      }
    }
    if (out.length === 0) out.push(this.response.msg || this.message);
    return out;
  }
}
